// ProfileAboutSwag.jsx
import React, { PureComponent } from 'react';
import PropTypes from 'prop-types';
import styled, { css } from 'styled-components';
import { withTranslation } from 'react-i18next';

import MailTo from '../container/MailTo.js';

import { getIsInPwa } from '../resource/getUserAgent.js';
import { TranslationNamespace } from '../resource/translationNamespace.js';
import env from '../resource/env.js';

import SwagLogo from '../../img/img_login_swag_lg.svg';
import BackIcon from '../../img/ic_topbar_back.svg';
const HORIZONTAL_PADDING = 16;

export class ProfileAboutSwag extends PureComponent {
  state = {
    expandChannel: [],
  };
  render() {
    const { pusherConnectionState, pusherSubscribedStates, close, t, utm } =
      this.props;
    const { expandChannel } = this.state;
    const version = env.TAG_NAME;
    const shaString = env.SHORT_SHA ? `(${env.SHORT_SHA})` : '';
    const branchString = env.BRANCH_NAME ? `(${env.BRANCH_NAME})` : '';
    const isOnTagVersion = !/-/.test(version);
    const hasFlavor = !!utm?.['_flavor'];
    return (
      <StyledProfileAboutSwag>
        <Header>
          <CloseButton
            role="button"
            onClick={() => {
              close();
            }}
          >
            <CloseIcon src={BackIcon} title="close" />
          </CloseButton>
          <HeaderTitle>
            {t('about_swag', {
              ns: TranslationNamespace.SETTINGS,
            })}
          </HeaderTitle>
        </Header>
        <Body>
          <AboutSection>
            <LogoWrapper>
              <LogoImage src={SwagLogo} alt="logo" />
              <DescriptionWrapper>
                {t('swag_description', {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </DescriptionWrapper>
            </LogoWrapper>
            <TextBlocks>
              <TextBlock>
                <VersionAndCopyright>
                  Web App {getIsInPwa() ? '(PWA)' : ''}
                  {hasFlavor ? (
                    <>
                      <br />
                      Flavor: {utm['_flavor']}
                    </>
                  ) : null}
                  <br />
                  {`${version} ${isOnTagVersion ? shaString : branchString}`}
                  <br />© SWAG{' '}
                  {t('setting_about_version3', {
                    ns: TranslationNamespace.SETTINGS,
                  })}
                </VersionAndCopyright>
              </TextBlock>
              <TextBlock>
                <p>
                  {t('setting_about_more_info', {
                    ns: TranslationNamespace.SETTINGS,
                  })}
                </p>
                <Anchor
                  target="_blank"
                  rel="noreferrer noopener"
                  href="https://swag.live"
                  title="https://swag.live"
                />
              </TextBlock>
              <TextBlock>
                <p>
                  {t('contact_support', {
                    ns: TranslationNamespace.SETTINGS,
                  })}
                </p>
                <MailToAnchor forwardedAs="a" title="support@swag.live" />
              </TextBlock>
            </TextBlocks>
          </AboutSection>
          <ConnectionStatus>
            <li>
              <ChannelName>Pusher</ChannelName>
              <StateText>
                {t(`pusher_${pusherConnectionState}`, {
                  ns: TranslationNamespace.SETTINGS,
                })}
              </StateText>
            </li>
            {pusherSubscribedStates &&
              Object.keys(pusherSubscribedStates).map(channelName => {
                if (
                  !/^presence-enc-user@|^presence-enc-client@/.test(channelName)
                )
                  return null;
                if (!pusherSubscribedStates?.[channelName]?.subscribed)
                  return null;
                const isExpanded = expandChannel.includes(channelName);
                return (
                  <li
                    key={channelName}
                    onClick={() =>
                      this.setState(() => {
                        if (channelName.search(/@/) === -1) return;
                        const expand = [...expandChannel];
                        const index = expand.indexOf(channelName);
                        if (index === -1) {
                          expand.push(channelName);
                        } else {
                          expand.splice(index, 1);
                        }
                        return {
                          expandChannel: expand,
                        };
                      })
                    }
                  >
                    <ChannelName isExpanded={isExpanded}>
                      {channelName}
                    </ChannelName>
                    <StateText>
                      {t('pusher_connected', {
                        ns: TranslationNamespace.SETTINGS,
                      })}
                    </StateText>
                  </li>
                );
              })}
          </ConnectionStatus>
        </Body>
      </StyledProfileAboutSwag>
    );
  }
}

ProfileAboutSwag.propTypes = {
  pusherConnectionState: PropTypes.string,
  pusherSubscribedStates: PropTypes.object,
  close: PropTypes.func,
  t: PropTypes.func.isRequired,
  utm: PropTypes.object,
};

ProfileAboutSwag.defaultProps = {
  pusherConnectionState: 'disconnected',
  pusherSubscribedStates: {},
  close: () => null,
  utm: {},
};

const StyledProfileAboutSwag = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: #191919;
`;

const Header = styled.div`
  flex: none;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  border-bottom: 1px solid rgb(100, 100, 100, 0.5);
  height: 58px;
  background-color: #232323;
`;

const CloseButton = styled.div`
  box-sizing: content-box;
  position: absolute;
  top: 0;
  left: 0;
  padding: 14px ${HORIZONTAL_PADDING}px;
  cursor: pointer;
  height: 30px;
  :hover {
    opacity: 0.7;
  }
`;

const AboutSection = styled.div`
  position: relative;
  border-top: 1px solid rgba(100, 100, 100, 0.5);
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  margin-top: 32px;
  padding: 32px 0;
  background-color: #232323;
`;

const CloseIcon = styled.img`
  height: 100%;
  width: auto;
  vertical-align: top;
`;

const HeaderTitle = styled.div`
  flex: none;
  margin: 0px;
  font-size: 16px;
  color: #ffffff;
`;

const Body = styled.div`
  overflow-y: auto;
`;

const LogoWrapper = styled.div`
  width: 100%;
  margin-bottom: 28px;
  display: flex;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  & > img {
    width: 100px;
  }
`;

const LogoImage = styled.img``;

const DescriptionWrapper = styled.p`
  margin-top: 12px;
  margin-bottom: 0px;
  font-size: 14px;
  color: #c8c8c8;
  white-space: pre;
  text-align: center;
  line-height: calc(1em + 8px);
`;

const TextBlocks = styled.div`
  margin-bottom: 40px;
  &:nth-last-child(1) {
    margin-bottom: 0px;
  }
`;

const TextBlock = styled.div`
  margin-bottom: 20px;
  width: 100%;
  font-size: 14px;
  text-align: center;

  p {
    margin: 0px;
    margin-bottom: 8px;
  }
  &:nth-last-child(1) {
    margin-bottom: 0px;
  }
`;

const VersionAndCopyright = styled.div`
  line-height: 1.43;
`;

const StyledAnchor = css`
  color: #00ffe9;

  &::before {
    content: attr(title);
  }
`;

const Anchor = styled.a`
  ${StyledAnchor}
`;

const MailToAnchor = styled(MailTo)`
  ${StyledAnchor}
`;

const ConnectionStatus = styled.ul`
  list-style: none;
  margin: 32px 0 36px;
  border-top: 1px solid rgba(100, 100, 100, 0.5);
  border-bottom: 1px solid rgba(100, 100, 100, 0.5);
  background-color: #232323;
  padding: 0;
  padding-left: 16px;
  padding-right: 16px;
  & > li {
    display: flex;
    width: 100%;
    min-height: 44px;
    border-bottom: 1px solid rgba(100, 100, 100, 0.5);
    padding: 14px 0;
    font-size: 16px;
    color: #ffffff;
    white-space: nowrap;
    overflow-x: scroll;
    -ms-overflow-style: none;
    &:nth-last-child(1) {
      border: none;
    }
    &::-webkit-scrollbar {
      display: none;
    }
  }
`;

const ChannelName = styled.span`
  position: relative;
  flex: auto;
  cursor: pointer;
  text-overflow: ellipsis;
  overflow: ${({ isExpanded }) => (isExpanded ? 'visible' : 'hidden')};
`;

const StateText = styled.span`
  flex: none;
  margin-left: 16px;
  font-size: 16px;
  line-height: 1;
  color: #646464;
`;

export default withTranslation()(ProfileAboutSwag);
